/*eslint-disable camelcase*/
import React, { useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import PropTypes from 'prop-types';

import { makeStyles, useTheme } from '@mui/styles';

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddBoxIcon from '@mui/icons-material/AddBox';
import IconButton from '@mui/material/IconButton';
import Fade from '@mui/material/Fade';
import Popper from '@mui/material/Popper';
import Box from '@mui/material/Box';
import DialogTitle from '@mui/material/DialogTitle';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { Menu, MenuItem, Collapse, } from '@mui/material';
import { Typography } from '@mui/material';
import TreeView from './Treeview';
import NodeExpression from './nodeexpression';
import NodeOperator from './nodeoperator';


import {
    ChevronRight,
    ExpandMore,

    DragIndicator,
    Add,
    Delete
} from '@mui/icons-material';


import { useValidate, checkValidity } from '../validator';
// import { useWhatChangedEffect } from '../../libraries/withwhyrender';

import StyledPaper from '../styled/paper';
import { NormalNode } from './utils';

import DataContext from '../../data/data-context';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: '2px 4px',
        display: 'flex',
        alignItems: 'center',
        width: 400,
    },
    titlebar: {
        padding: 0,
        position: 'absolute',
        top: '0',
        right: '0',
        zIndex: '1000'
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500]
    },
    applyButton: {
        position: 'absolute',
        right: theme.spacing(6),
        top: theme.spacing(1),
    },
    iconButton: {
        padding: 10,
    },
    filterBuilder: {
        padding: '2px 4px',
        display: 'flex',
        overflow: 'hidden',
        maxHeight:'85vh',
        position: 'relative',
        zIndex: theme.zIndex.modal,
    },
    scrollable: {
        display: 'flex',
        flexDirection: 'column', 
        overflow: 'hidden',
        position: 'relative',
        maxHeight: '70vh',
        minHeight: 65,
        overflowY: 'auto',
        minWidth: 700,
    },
    filterTree: {
        '& .rst__nodeContent': {
            position: 'absolute',
        },
        '& .rst__lineHalfHorizontalRight': {
            '&:before': {
                backgroundColor: theme.palette.divider,
            }
        },

        '& .rst__lineFullVertical': {
            '&:after': {
                backgroundColor: theme.palette.divider,
            }
        },
        '& .rst__lineHalfVerticalTop:': {
            '&:after': {
                backgroundColor: theme.palette.divider,
            }
        },
        '& .rst__lineHalfVerticalBottom': {
            '&:after': {
                backgroundColor: theme.palette.divider,
            }
        },
        '& .rst__lineChildren': {
            '&:after': {
                backgroundColor: theme.palette.divider,
            }
        },
    }
}));

// incoming transformation (from filter builder to filter)
const ToFilter = treeData => {
    let f;
    if (Array.isArray(treeData)) {
        f = JSON.parse(JSON.stringify(treeData[0]));
    } else {
        f = JSON.parse(JSON.stringify(treeData));
    }
    return { ...EMPTY_FILTER, ...Denormalize(f) };
};

const Denormalize = treeData => {
    if (!treeData) return treeData;
    if (treeData.children) {
        treeData.nodes = (treeData.children || []).map(Denormalize);
    } else {
        treeData.value = `${treeData.value}`;
    }
    // get rid of columns we don't need
    const {
        children,
        root,
        hidden,
        title,
        groupable,
        fivetuple,
        country,
        field,
        indexed,
        tableHidden,
        jsonHidden,
        last_seen_in_index,
        last_seen,
        hide,
        source,
        name,
        align,
        gator,
        hint,
        jsonSort,
        props,
        valueType,
        select,
        ...rest
    } = treeData;
    return { ...rest };
};

const generateNodeId = (prefix = 'node') => {
    return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
};


const setupTreeWithIds = (nodes) => {
    if (!nodes) return [];

    const addIdsToTree = (nodes) => {
        if (!Array.isArray(nodes)) {
            return addIdsToTree([nodes])[0];
        }

        const nodesWithIds = JSON.parse(JSON.stringify(nodes));
        nodesWithIds.forEach(node => {
            if (!node.id) {
                node.id = generateNodeId();
            }
            if (node.children && node.children.length > 0) {
                node.children = addIdsToTree(node.children);
            }
        });
        return nodesWithIds;
    };
    return addIdsToTree(nodes);
};

const EMPTY_FILTER = { operator: 'And', root: true, nodes: [] };
const nodewidth = 530;
const nodeheight = 57;
const indent = 30;

const FilterBuilder = ({
    filter: filterProp = EMPTY_FILTER,
    show: showProp = false,
    placement = 'bottom-start',
    ...props
}) => {
    const classes = useStyles();
    const theme = useTheme();
    const [expanded, setExpanded] = useState(['root']);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const inputRef = useRef(null);
    const { mtdtMappings, alertMappings } = useContext(DataContext);

    const [filter, setFilter] = useState();
    const [show, setShow] = useState(false);
    const [hideTimeout, setHideTimeout] = useState(420);
    const [modified, setModified] = useState(false);
    const [defaultVar, setDefaultVar] = useState({});
    const [fields, setFields] = useState([]);
    const [mappings, setMappings] = useState({});
    const [kick, setKick] = useState(Math.random());
    const filterForm = useValidate({});
    const MUI_X_PRODUCTS = [
        {
            "operator": "And",
            "children": [],
            "expanded": true,
            "root": true
        }
    ];

    const calcSize = useCallback(() => {
        if (!filter || filter.length < 1) return nodewidth;
        const getTreeSize = n => {
            if (!n || !n.expanded || !n.children || !n.children.length) return { depth: 0, height: 0 };
            return {
                depth: Math.max(...(n.children.map(c => getTreeSize(c).depth))) + 1,
                height: n.children.reduce((a, c) => a + 1 + getTreeSize(c).height, 0),
            };
        };
        const { depth, height } = getTreeSize(filter[0]);
        return {
            width: nodewidth + depth * indent + theme.getSpacing(1),
            height: (height + 1) * nodeheight + theme.getSpacing(1),
        };
    }, [filter, theme]);

    const normalize = useCallback(f => {
        if (!f) return f;
        if (f.nodes) {
            f.children = (f.nodes || []).map(normalize);
            // expand the node unless specified
            f.expanded = f.expanded === undefined || f.expanded;
        }
        // get rid of columns we don't need
        const {
            nodes,
            root,
            hidden,
            title,
            groupable,
            fivetuple,
            sortable,
            select,
            country,
            field,
            indexed,
            tableHidden,
            jsonHidden,
            last_seen_in_index,
            last_seen,
            hide,
            source,
            name,
            align,
            gator,
            hint,
            jsonSort,
            props,
            valueType,
            ...rest
        } = f;
        return NormalNode(rest);
    }, []);

    // outgoing transformation (from filter to filter builder)
    // - can't use sortable-tree 'map' for these because it assumes children are called 'children'
    const toTreeData = useCallback(f => {
        // defese against the dark arts of invalid incoming filters
        const treeData = JSON.parse(JSON.stringify({ ...EMPTY_FILTER, ...f }));
        return [{ ...normalize(treeData), root: true }];
    }, [normalize]);

    // used to determine modified flag
    const sanitize = useCallback(f => {
        // get rid of columns that don't impact data
        const { root, expanded, type, value, id, _uniqueTitle, ...rest } = f || {};

        if (rest.nodes) {
            rest.nodes = rest.nodes.map(sanitize);
            // put in the right order
            return { ...{ operator: 'And', nodes: [] }, ...rest };
        }

        // put in the right order and add operator
        return {
            ...{ variable: '', value: '', type: '', operator: '=' },
            ...{ ...rest, value: `${value}` }
        };
    }, []);

    const publishUpdates = f => {
        if (!f || f.length < 1) return;
        props.onOK({ filter: ToFilter(f) });
    };


    const handleOnShow = () => {
        setHideTimeout(420);
        props.onShow(!show);
    };

    const handleOnHide = e => {
        e.stopPropagation();
        setHideTimeout(420);
        props.onShow(false);
    };

    const handleOnSearch = e => {
        e.stopPropagation();
        publishUpdates(filter);
        // should I show or should I hide?
        // if I show there will be trouble,
        // and if I hide there will be double

        // setHideTimeout(420);
        // props.onShow(false);
    };

    // keep kick in the list of dependencies to force a re-render

    // modified hook
    useEffect(() => {
        if (!filter) {
            return;
        }
        const newf = JSON.stringify(sanitize(ToFilter(filter)));
        const oldf = JSON.stringify(sanitize(filterProp));
        setModified(newf !== oldf);
        setKick(Math.random());
    }, [filter, filterProp, sanitize]);

    useEffect(() => {
        setFilter(setupTreeWithIds(toTreeData(filterProp)));
    }, [filterProp, toTreeData]);

    // incoming open/close
    useEffect(() => {
        setShow(showProp);
    }, [showProp]);

    useEffect(() => {
        setMappings({ ...alertMappings, ...mtdtMappings });
    }, [alertMappings, mtdtMappings]);

    useEffect(() => {
        const filterables = props.fields
            .filter(c => c.indexed && c.type !== 'timestamp' && c.type !== 'date')
            .sort((a, b) => a.title.toLowerCase() < b.title.toLowerCase() ? -1 : 1);
        setFields(filterables);
        setDefaultVar(filterables[Object.keys(filterables)[0]]);
    }, [props.fields]);


    useEffect(() => {
        // temporary open with fadeway
        // if the dialog is already open this has no affect
        // must be called with a new object pointer or different value each time
        if (show || !props.showTimeout || !props.showTimeout.exit) return;

        setShow(true);
        setTimeout(() => {
            setHideTimeout(props.showTimeout.exit);
            setShow(false);
            // reset timout back to normal
            setTimeout(() => {
                setHideTimeout(420);
            }, props.showTimeout.exit);
        }, 2100);
    }, [props.showTimeout]);


    const handleOnChange = useCallback((nodeId, nodeData, validID, valid) => {
        if (!nodeId) return;
        try {
            const daForm = { ...filterForm.get(), [validID]: valid };
            if (validID) filterForm.set(daForm);
            setKick(Math.random());

            const updateNode = (nodes) => nodes.map((node) => {
                if (!nodes) return [];
                if (node.id === nodeId) {
                    // Check if we're passing the full node object or just a patch
                    return nodeData?.id === nodeId
                        ? nodeData
                        : { ...node, ...nodeData };
                }
                if (node.children) {
                    return { ...node, children: updateNode(node.children) };
                }
                return node; 
            });
            setFilter((prev) => updateNode(prev));

        } catch (err) {
            console.error(err);
        }
    }, [setFilter, filterForm]);

    const isValid = useMemo(() => {
        const formData = filterForm.get();
        return modified && (Object.keys(formData).length > 0 ? checkValidity(formData) : true);
    }, [modified, filterForm, kick]);

    const handleAddNode = (parentId, nodeType) => {
        setFilter(prevNodes => {
            const newNodes = JSON.parse(JSON.stringify(prevNodes)); // Deep clone

            const newNodeId = generateNodeId();

            let newNode = nodeType === 'group'
                ? { id: newNodeId, operator: 'AND', children: [], expanded: true }
                : { id: newNodeId, variable: defaultVar.variable , field: '', operator: '=', value: '' };

            const addToNode = (nodes) => {
                for (const node of nodes) {
                    if (node.id === parentId && node.children) {
                        node.children.push(newNode);
                        return true;
                    }
                    if (node.children && addToNode(node.children)) return true;
                }
                return false;
            };

            addToNode(newNodes);
            return newNodes;
        });
    };

    const handleToggleExpand = useCallback((id) => {
        const updateNode = (nodes) => {
            return nodes.map(node => {
                if (node.id === id) {
                    return { ...node, expanded: !node.expanded };
                }
                if (node.children) {
                    return { ...node, children: updateNode(node.children) };
                }
                return node;
            });
        };

        setFilter(prevItems => updateNode(prevItems));
    });

    const handleDeleteNode = (nodeId, validID) => {
        const daForm = { ...filterForm.get() };
        delete daForm[validID];
        if (validID) filterForm.set(daForm);
        setKick(Math.random());

        setFilter(prevNodes => {
            const deleteRecursively = (nodes) => {
                return nodes
                    .map(node => {
                        // If this node matches the nodeId, remove it
                        if (node.id === nodeId) return null;
                        // Otherwise, recurse into children if they exist
                        if (node.children) {
                            node.children = deleteRecursively(node.children);
                        }

                        return node;
                    })
                    .filter(Boolean); // Remove nulls
            };

            const updated = deleteRecursively(prevNodes);
            return updated;
        });
    };

    const handleValidateNode = useCallback((validID, valid) => {

        if (!validID) return;
        const form = filterForm.get();

        form[validID] = valid;
        validID && filterForm.set(form);
        setKick(Math.random());
    }, [filterForm]);



    const moveTreeNode = (nodes, sourceId, targetId) => {
        const newNodes = JSON.parse(JSON.stringify(nodes));

        const findNodeAndParent = (nodes, id, parent = null) => {
            for (let i = 0; i < nodes.length; i++) {
                const node = nodes[i];

                // Only non-leaf (group) nodes should have children
                if (!node.variable && !node.children) {
                    node.children = [];
                }

                if (node.id === id) return { node, parent, index: i };

                if (node.children && node.children.length > 0) {
                    const result = findNodeAndParent(node.children, id, node);
                    if (result.node) return result;
                }
            }
            return { node: null, parent: null, index: -1 };
        };

        const { node: sourceNode, parent: sourceParent, index: sourceIndex } = findNodeAndParent(newNodes, sourceId);
        const { node: targetNode } = findNodeAndParent(newNodes, targetId);

        // Don't move root or leaf nodes into other leaf nodes
        if (!sourceNode || !targetNode || sourceNode.root || targetNode.variable) return newNodes;

        // Remove from current position
        if (sourceParent) {
            sourceParent.children.splice(sourceIndex, 1);
            if (!sourceParent.variable && !sourceParent.children.length) {
                sourceParent.children = [];
            }
        } else {
            newNodes.splice(sourceIndex, 1);
        }

        // Add to new position
        if (!targetNode.children) targetNode.children = [];
        targetNode.children.unshift(sourceNode);

        return newNodes;
    };


    const handleMove = (sourceId, targetId) => {
        setFilter(moveTreeNode(filter, sourceId, targetId));
    };
    return (
        <React.Fragment>
            {props.button
                ? React.cloneElement(
                    props.button,
                    { ref: inputRef, onClick: handleOnShow },
                )
                : <IconButton
                    title="filter"
                    ref={inputRef}
                    className={classes.iconButton}
                    aria-label="make filter"
                    onClick={handleOnShow}
                    size="large">
                    <AccountTreeIcon />
                </IconButton>
            }
            <Popper
                className={classes.filterBuilder}
                open={show}
                anchorEl={inputRef.current}
                placement={placement}
                role={undefined} transition disablePortal
                modifiers={[
                    { name: 'flip', options: { enabled: true } },
                    { name: 'preventOverflow', options: { enabled: true, boundary: 'window' } },
                ]}>  
                {({ TransitionProps, placement }) => (
                    <Fade {...TransitionProps} timeout={{ enter: 420, exit: hideTimeout }}>
                        <StyledPaper>
                            <Box className={classes.scrollable}>
                                <DndProvider backend={HTML5Backend}>
                                    <Box sx={{ minWidth: 350 }}>
                                        {filter.map((node) => (
                                            <TreeView
                                                key={node.id}
                                                fields={fields}
                                                node={node}
                                                mappings={mappings}
                                                onAdd={handleAddNode}
                                                onToggleExpand={handleToggleExpand}
                                                onChange={handleOnChange}
                                                onDelete={handleDeleteNode}
                                                onMove={handleMove}
                                                onValidate={handleValidateNode}
                                            />
                                        ))}
                                    </Box>
                                </DndProvider>

                            </Box>
                            {/* this shows up above but want it rendered layered on top of the tree */}
                            <DialogTitle className={classes.titlebar}>
                                <IconButton
                                    color="primary"
                                    title="apply"
                                    aria-label="apply"
                                    disabled={!isValid}
                                    className={classes.applyButton}
                                    onClick={handleOnSearch}
                                    size="large">
                                    <DoneIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    aria-label="close"
                                    title="close"
                                    className={classes.closeButton}
                                    onClick={handleOnHide}
                                    size="large">
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </DialogTitle>
                        </StyledPaper>
                    </Fade>
                )}
            </Popper>
        </React.Fragment>
    );
};

FilterBuilder.propTypes = {
    fields: PropTypes.array.isRequired,
    show: PropTypes.bool,
    showTimeout: PropTypes.shape({
        exit: PropTypes.number.isRequired
    }),
    filter: PropTypes.object,
    onShow: PropTypes.func.isRequired,
    onOK: PropTypes.func.isRequired,
    placement: PropTypes.string,
    button: PropTypes.node,
};

export {
    FilterBuilder as default,
    ToFilter,
    EMPTY_FILTER
};
